import type { NewsItem } from '../../types/news.ts'
import { isPublisherSponsored } from '../../services/news/presentation.ts'

export type NewsExperienceMode = 'home' | 'full'
export type NewsSourceType = 'independent' | 'official' | 'vendor' | 'preprint'

const officialSourceIds = new Set([
  'doe', 'eia', 'iso-ne', 'uk-dsit', 'eu-digital', 'south-africa-dsti', 'korea-msit',
  'cisa-advisories', 'cisa-news', 'uk-ncsc', 'cert-eu', 'jpcert-en',
  'cmu-robotics', 'mit-robotics',
])

const vendorSourceIds = new Set([
  'nvidia-blog', 'aws-ml-blog', 'google-ai', 'openai-news', 'hugging-face',
  'nvidia-robotics', 'google-deepmind', 'robotiq', 'nvidia-developer-robotics', 'aws-physical-ai',
  'microsoft-security', 'google-security', 'project-zero', 'cloudflare-security', 'cisco-talos', 'unit-42',
])

const preprintPattern = /(^|[-_.\s])arxiv($|[-_.\s])|\bpreprint\b/i
const vendorMaterialPattern = /\b(white[ -]?papers?|sponsored|advertorial|partner content|paid post|brand voice)\b/i
const genericSourceArtPattern = /(?:^|[/_.-])(logo|favicon|avatar|icon)(?:[/_.-]|$)|page[-_ ]?0*001|white[ -]?papers?/i

const sourceText = (item: NewsItem) => `${item.sourceId} ${item.publisher} ${item.url}`
const storyText = (item: NewsItem) => `${item.title} ${item.summary ?? ''} ${item.url}`

export interface NewsSourcePresentation {
  type: NewsSourceType
  label: string
  description: string
}

export function getNewsSourcePresentation(item: NewsItem): NewsSourcePresentation {
  if (item.kind === 'ai-synthesis') return { type: 'independent', label: 'OMIKINA ANALYSIS', description: 'Source-linked OMIKINA editorial analysis.' }
  if (preprintPattern.test(sourceText(item))) return { type: 'preprint', label: 'PREPRINT · NOT PEER REVIEWED', description: 'Research discovery signal; inspect the paper and review status.' }
  if (vendorSourceIds.has(item.sourceId) || vendorMaterialPattern.test(storyText(item))) return { type: 'vendor', label: 'VENDOR SOURCE', description: 'First-party commercial or promotional material; verify claims independently.' }
  if (officialSourceIds.has(item.sourceId)) return { type: 'official', label: 'OFFICIAL SOURCE', description: 'Primary institution, agency, or operator publication.' }
  return { type: 'independent', label: 'INDEPENDENT REPORTING', description: 'Independent newsroom, trade, specialist, or research publication.' }
}

export function isNewsLeadEligible(item: NewsItem): boolean {
  const source = getNewsSourcePresentation(item)
  return !isPublisherSponsored(item) && source.type !== 'vendor' && source.type !== 'preprint'
}

export function orderNewsForEligibleLead(items: NewsItem[], now = Date.now()): NewsItem[] {
  const activeEditorialLead = items.filter((item) => {
    if (item.kind !== 'ai-synthesis' || !item.homepageLeadUntil || !isNewsLeadEligible(item)) return false
    const publishedAt = Date.parse(item.publishedAt)
    const leadUntil = Date.parse(item.homepageLeadUntil)
    return Number.isFinite(publishedAt) && publishedAt <= now && Number.isFinite(leadUntil) && leadUntil > now
  }).sort((left, right) => Date.parse(right.publishedAt) - Date.parse(left.publishedAt) || left.id.localeCompare(right.id))[0]
  const leadIndex = activeEditorialLead ? items.findIndex((item) => item.id === activeEditorialLead.id) : items.findIndex(isNewsLeadEligible)
  if (leadIndex <= 0) return items
  return [items[leadIndex], ...items.slice(0, leadIndex), ...items.slice(leadIndex + 1)]
}

export function shouldUseNewsSourceImage(item: NewsItem): boolean {
  if (!item.imageUrl) return false
  if (getNewsSourcePresentation(item).type === 'preprint') return false
  if (vendorMaterialPattern.test(storyText(item))) return false
  return !genericSourceArtPattern.test(item.imageUrl)
}

export interface CuratedHomeNews {
  primaryItems: NewsItem[]
  researchItems: NewsItem[]
  leadItemId: string | null
}

export function curateHomeNews(items: NewsItem[], primaryLimit = 4, researchLimit = 2, now = Date.now()): CuratedHomeNews {
  const researchItems = items.filter((item) => getNewsSourcePresentation(item).type === 'preprint').slice(0, researchLimit)
  const candidates = orderNewsForEligibleLead(items.filter((item) => getNewsSourcePresentation(item).type !== 'preprint' && !isPublisherSponsored(item)), now)
  const primaryItems: NewsItem[] = []
  const usedSources = new Set<string>()

  for (const item of candidates) {
    if (primaryItems.length >= primaryLimit) break
    if (usedSources.has(item.sourceId)) continue
    primaryItems.push(item)
    usedSources.add(item.sourceId)
  }
  for (const item of candidates) {
    if (primaryItems.length >= primaryLimit) break
    if (primaryItems.some((candidate) => candidate.id === item.id)) continue
    primaryItems.push(item)
  }

  return {
    primaryItems,
    researchItems,
    leadItemId: primaryItems.find(isNewsLeadEligible)?.id ?? null,
  }
}
